// Canvas — visual port of Inku's /canvas surface. Stubbed: shows a grid
// of seeded canvases with thumbnails (gradient placeholders), each opening
// a static "canvas detail" placeholder. TLDraw integration is a real-Inku
// feature we're not wiring functionally for the demo.

import { Edit3, Plus, Sparkles } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

interface CanvasSeed {
  id: string;
  title: string;
  brand: string;
  updatedAt: string;
  thumbnail: string;
  imagesCount: number;
}

const CANVASES: CanvasSeed[] = [
  {
    id: "c_brame_jan",
    title: "BRAME · Dry January launch",
    brand: "BRAME",
    updatedAt: "2h ago",
    thumbnail: "linear-gradient(135deg, #3502F6 0%, #B5A4FF 60%, #FFD08A 100%)",
    imagesCount: 47,
  },
  {
    id: "c_loop_q2",
    title: "Loop Foods · Q2 retail rollout",
    brand: "Loop Foods",
    updatedAt: "yesterday",
    thumbnail: "linear-gradient(135deg, #2E7D32 0%, #76C893 60%, #F2EBC1 100%)",
    imagesCount: 23,
  },
  {
    id: "c_veldt_drop",
    title: "Veldt · bottle drop concepts",
    brand: "Veldt",
    updatedAt: "3d ago",
    thumbnail: "linear-gradient(135deg, #6E2A14 0%, #C7935E 60%, #F3EADB 100%)",
    imagesCount: 12,
  },
  {
    id: "c_brame_summer",
    title: "BRAME · summer occasions",
    brand: "BRAME",
    updatedAt: "1w ago",
    thumbnail: "linear-gradient(135deg, #FFB17A 0%, #FF8FA0 50%, #5A2BFF 100%)",
    imagesCount: 31,
  },
  {
    id: "c_loop_pkg",
    title: "Loop Foods · packaging refresh",
    brand: "Loop Foods",
    updatedAt: "2w ago",
    thumbnail: "linear-gradient(135deg, #BBE9FF 0%, #C4B6FF 50%, #FFC8DD 100%)",
    imagesCount: 18,
  },
  {
    id: "c_brame_pulse",
    title: "BRAME · brand pulse Jan",
    brand: "BRAME",
    updatedAt: "1mo ago",
    thumbnail: "linear-gradient(135deg, #1A007F 0%, #3502F6 60%, #6B4FFF 100%)",
    imagesCount: 64,
  },
];

export function CanvasPage() {
  const { setToast, route } = useStore();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (route.canvasId) {
    const canvas = CANVASES.find((c) => c.id === route.canvasId) ?? CANVASES[0];
    return <CanvasDetail canvas={canvas} />;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 py-12 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Workspace
          </p>
          <h1 className="font-freight text-5xl font-light tracking-tight leading-none">
            Canvas
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Visual workspaces for each campaign. Drop assets, generate
            variations, brief the agent on what to push next.
          </p>
        </div>
        <Button
          onClick={() =>
            setToast({
              kind: "ok",
              text: "Canvas creation is not wired up in this demo.",
            })
          }
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          New canvas
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CANVASES.map((c) => (
          <button
            key={c.id}
            onClick={() => (window.location.hash = `#/canvas/${c.id}`)}
            onMouseEnter={() => setHoveredId(c.id)}
            onMouseLeave={() => setHoveredId(null)}
            className="text-left group rounded-xl border border-border bg-card overflow-hidden shadow-card hover:shadow-card-hover transition-shadow"
          >
            <div
              className={cn(
                "aspect-[4/3] relative overflow-hidden",
                "transition-transform duration-500"
              )}
              style={{ background: c.thumbnail }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-wider uppercase text-white/90 bg-black/30 backdrop-blur-sm rounded px-2 py-0.5">
                  {c.imagesCount} images
                </span>
                {hoveredId === c.id && (
                  <span className="font-mono text-[10px] tracking-wider uppercase text-white/90 bg-black/30 backdrop-blur-sm rounded px-2 py-0.5">
                    open ↗
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 space-y-1">
              <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                {c.brand}
              </p>
              <h3 className="text-sm font-medium leading-snug line-clamp-2">
                {c.title}
              </h3>
              <p className="text-xs text-muted-foreground/70 pt-1">
                Updated {c.updatedAt}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CanvasDetail({ canvas }: { canvas: CanvasSeed }) {
  const { navigate, setToast } = useStore();
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="border-b border-border bg-background">
        <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 py-5 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <button
              onClick={() => navigate("/canvas")}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              ← All canvases
            </button>
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              {canvas.brand}
            </p>
            <h1 className="font-freight text-3xl font-light tracking-tight leading-tight">
              {canvas.title}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() =>
                setToast({
                  kind: "ok",
                  text: "Editing is not wired up in this demo.",
                })
              }
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Edit3 className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              onClick={() =>
                setToast({
                  kind: "ok",
                  text: "Generation is not wired up in this demo.",
                })
              }
              size="sm"
              className="gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Generate
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-canvas">
        <div
          className="h-full w-full relative"
          style={{
            backgroundImage:
              "radial-gradient(circle, hsl(var(--canvas-grid)) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        >
          {/* Pretend "shapes" floating on the canvas */}
          <div className="absolute top-[15%] left-[10%] w-64 aspect-[4/5] rounded-lg shadow-card-hover ring-1 ring-[hsl(var(--inku-blue))/.4]"
               style={{ background: canvas.thumbnail }} />
          <div className="absolute top-[20%] left-[35%] w-72 aspect-[5/4] rounded-lg shadow-card"
               style={{ background: canvas.thumbnail, filter: "hue-rotate(40deg)" }} />
          <div className="absolute top-[55%] left-[20%] w-56 aspect-square rounded-lg shadow-card"
               style={{ background: canvas.thumbnail, filter: "hue-rotate(80deg)" }} />
          <div className="absolute top-[40%] right-[15%] w-80 aspect-[4/3] rounded-lg shadow-card"
               style={{ background: canvas.thumbnail, filter: "hue-rotate(120deg) brightness(1.1)" }} />
          <div className="absolute top-[10%] right-[20%] w-44 aspect-[3/4] rounded-lg shadow-card"
               style={{ background: canvas.thumbnail, filter: "hue-rotate(180deg)" }} />

          <div className="absolute bottom-6 right-6 rounded-lg bg-card/90 backdrop-blur-md border border-border shadow-card px-4 py-2 text-xs text-muted-foreground italic">
            TLDraw canvas — visual stub for demo
          </div>
        </div>
      </div>
    </div>
  );
}
